import { fromJS } from "immutable";
import { format, formatDistanceToNow, isValid } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import AssigneeSelectV2, { toIdArray } from "../components/AssigneeSelect";
import ProgressField from "./ProgressField";
import DropdownField from "../board/components/DropdownField";
import CustomField from "../main/CustomField";
import LinkItemPickerV2, { renderItemIcon } from "../main/component/LinkItemPickerV2";
import InputInline from "../components/InputInline";
import EditableTextCell from "./EditableTextCell";
import { InformationCircleIcon, UserIcon } from "@heroicons/react/24/outline";
import AmountField from "../components/AmountField";
import LabelsSelect from "../components/LabelsSelect";
import { useQuery } from "../AppLayout";
import { ActivityStatus } from "../activities/ActivityIndicator";
import React, { useState } from "react";
import Editor from "../components/Editor";
import ActionableField from "./ActionableField";
import { useNavigate } from "react-router-dom";
import { navigateToItem } from "../Navigator";
import SelectionField from "./SelectionField";
import { ContentTypes } from "../constants/action-types";
import ActivityAction from "../actions/ActivityAction";
import TaskAction from "../actions/TaskAction";
import HoverManager from "../components/HoverManager";
import LayoutAction from "../actions/LayoutAction";
import DefaultAvatar from "../components/DefaultAvatar";
import EmailInputField from "../board/components/TaskDetail/EmailInputField";
import WebsiteInputField from "../board/components/TaskDetail/WebsiteInputField";
import { ImageIcon } from "lucide-react";
import ImageView from "../ai-content/components/ImageView";
import DomainsSelect from "../components/DomainsSelect";
import { colorCodes } from "../board/components/TaskDetail/ColorPicker";

type Props = {
  row: any;
  column: any;
  outlineNum?: string;
  workflowData?: any;
  editInline?: boolean;
  showAddRowFilter?: boolean;
  showSelectable?: boolean;
  onChangeFilter?: (filter: any) => void;
  onReload?: () => void;
};

export const renderStage = (stage) => {
  if (stage == "Lost") {
    return (
      <div className="inline-flex items-center rounded-full bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10">
        {"Lost"}
      </div>
    );
  }
  if (stage == "Unqualified") {
    return (
      <div className="inline-flex items-center rounded-full bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-inset ring-yellow-600/20">
        {"Unqualified"}
      </div>
    );
  }
  if (stage == "Won") {
    return (
      <div className="inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
        {"Won"}
      </div>
    );
  }
  return (
    <div className="inline-flex items-center rounded-full bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ">
      New
    </div>
  );
};

export const renderConnectionStrength = (strengthValue) => {
  let connectionData = {
    bgColor: "bg-red-100",
    dotColor: "bg-red-800",
    textColor: "text-red-800",
    text: "No Connection",
  };
  if (strengthValue > 10) {
    connectionData = {
      bgColor: "bg-green-100",
      dotColor: "bg-green-800",
      textColor: "text-green-800",
      text: "Very Strong",
    };
  } else if (strengthValue > 7) {
    connectionData = {
      bgColor: "bg-green-100",
      dotColor: "bg-green-700",
      textColor: "text-green-700",
      text: "Strong",
    };
  } else if (strengthValue > 4) {
    connectionData = {
      bgColor: "bg-yellow-100",
      dotColor: "bg-yellow-800",
      textColor: "text-yellow-800",
      text: "Moderate",
    };
  } else if (strengthValue > 2) {
    connectionData = {
      bgColor: "bg-orange-100",
      dotColor: "bg-orange-800",
      textColor: "text-orange-800",
      text: "Weak",
    };
  } else if (strengthValue > 0) {
    connectionData = {
      bgColor: "bg-red-100",
      dotColor: "bg-red-700",
      textColor: "text-red-700",
      text: "Very weak",
    };
  }
  return (
    <div
      className={`flex gap-[7px] items-center px-[3px] py-[1px] rounded-full ${connectionData.bgColor}`}
    >
      <div className={`${connectionData.dotColor} rounded-full w-1.5 h-1.5 shrink-0`}></div>
      <span
        className={`text-[10px] font-medium leading-4 tracking-tight ${connectionData.textColor} whitespace-nowrap`}
      >
        {connectionData.text}
      </span>
    </div>
  );
};

const CellRenderer = (props: Props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [emailError, setEmailError] = useState(false);
  const [websiteError, setWebsiteError] = useState(false);
  const {
    column,
    row,
    outlineNum = "",
    onChangeFilter,
    onReload,
    showAddRowFilter = false,
    showSelectable = false,
  } = props;
  const contentType = useQuery().get("type");
  const view = useQuery().get("view");
  const rowType = row.type || contentType;

  const buckets = useSelector((state: any) => state.bucketReducer.get(rowType)) || fromJS([]);
  const ruleReducer = useSelector((state: any) => state.ruleReducer);
  const fields = useSelector((state: any) => state.projectReducer.getIn(["project", "fields"]));
  const field = fields.find((item: any) => item.get("id") == column.get("selector"));

  const getRuleFieldValues = (fieldId) => {
    return ruleReducer
      .get(fieldId)
      .toJS()
      .map((item) => ({ id: item.value, name: item.label, color: item.color }));
  };

  const taskFilters = useSelector((state: any) => state.taskReducer.getIn(["filters"]));
  const handleAddFilter = (newFilter) => {
    onChangeFilter({
      ...taskFilters.toJS(),
      ...newFilter,
    });
  };

  const isActivity = () => {
    return rowType == ContentTypes.ACTIVITY;
  };

  const getDataPath = () => {
    const selector = column.get("selector");
    if (isActivity()) {
      if (selector == "item") {
        return ["event", "data", column.get("selector"), "label"];
      }
      return ["event", "data", column.get("selector")];
    }
    return ["data", column.get("selector"), "value"];
  };

  const renderView = () => {
    const rowObject = fromJS(row);

    const changeUser = async (users: any) => {
      const newUsers = users.map((user: any) => user.accountId);
      handleUpdateItem("assignees", newUsers);
    };

    const handleUpdateItem = (field: string, value: any) => {
      if (!isActivity()) {
        dispatch(TaskAction.updateTask(rowObject.get("id"), field, value, contentType, view));
      }
    };

    //CustomField
    if (column.get("isCustomField") == true) {
      if (field) {
        const customFieldValue = rowObject.getIn(["data", field.get("id"), "value"]);
        const fieldData = {
          ...field.toJS(),
          value: customFieldValue,
        };
        const customFieldId = column.get("id");
        return (
          <ActionableField
            showAddFilter={
              showAddRowFilter &&
              rowObject.get("type") !== "COMPANY" &&
              rowObject.get("type") !== "CONTACT"
            }
            onClickAddFilter={() => {
              handleAddFilter({
                [customFieldId]: {
                  operator: "is",
                  values: [customFieldValue],
                },
              });
            }}
          >
            <CustomField
              isGrid={true}
              field={fieldData}
              onChange={(valueChange: string | Date) => {
                handleUpdateItem(field.get("id"), valueChange);
              }}
            />
          </ActionableField>
        );
      }
    }

    let component = null;

    const currentVal = rowObject.getIn(getDataPath());
    const fieldId = column.get("selector");
    const showRowSelection = showSelectable && !isActivity();

    switch (fieldId) {
      case "id":
        component = (
          <div
            className={
              "w-full flex items-center truncate text-sm font-medium text-gray-700 leading-5 tracking-tight"
            }
          >
            {rowObject.get("id")}
          </div>
        );
        break;
      case "name":
        const isHoverableName =
          rowObject.get("type") === "COMPANY" || rowObject.get("type") === "CONTACT";
        const isSale = rowType === "SALE" || rowObject.get("type") === "SALE";
        const isProduct = rowType === "PRODUCT" || rowObject.get("type") === "PRODUCT";

        let renderAvatar;
        if (!isSale) {
          if (isProduct) {
            const imageIds = rowObject.getIn(["data", "imageIds", "value"]) || fromJS([]);
            if (imageIds.size > 0) {
              renderAvatar = (
                <ImageView fileName={imageIds.first()} className="w-20 h-20 object-cover p-1" />
              );
            } else {
              renderAvatar = <ImageIcon className="w-20 h-20 text-gray-300" />;
            }
          } else {
            renderAvatar = (
              <>
                {rowObject.get("iconUrl") ? (
                  <img
                    src={rowObject.get("iconUrl")}
                    alt="Account Logo"
                    className="w-5 h-5 rounded-lg object-cover ml-1.5"
                  />
                ) : (
                  <DefaultAvatar
                    name={rowObject.get("name")}
                    type={rowObject.get("type")}
                    size="sm"
                    className="ml-1.5"
                  />
                )}
              </>
            );
          }
        }

        component = (
          <ActionableField
            showAddFilter={
              showAddRowFilter &&
              rowObject.get("type") !== "COMPANY" &&
              rowObject.get("type") !== "CONTACT"
            }
            onClickAddFilter={() => {
              if (currentVal) {
                handleAddFilter({
                  [fieldId]: {
                    operator: "in",
                    values: [currentVal],
                  },
                });
              }
            }}
          >
            {isHoverableName ? (
              <HoverManager
                itemId={rowObject.get("id")}
                itemType={rowObject.get("type")}
                label={currentVal}
              >
                <div className={"flex items-center truncate w-full relative"}>
                  {showRowSelection && <SelectionField itemId={rowObject.get("id")} />}
                  {renderAvatar}
                  <EditableTextCell
                    value={currentVal || ""}
                    readOnly={isActivity()}
                    onClick={() => {
                      if (isActivity()) {
                        dispatch(
                          ActivityAction.showActivityDialog(
                            rowObject.get("itemId"),
                            rowObject.get("id")
                          )
                        );
                      } else {
                        navigateToItem({ item: rowObject, navigate });
                      }
                    }}
                    onEdit={(newValue) => {
                      if (currentVal != newValue) {
                        handleUpdateItem(fieldId, newValue);
                      }
                    }}
                  />
                </div>
              </HoverManager>
            ) : (
              <div className={"flex items-center truncate w-full relative"}>
                {showRowSelection && <SelectionField itemId={rowObject.get("id")} />}
                {renderAvatar}
                <EditableTextCell
                  value={currentVal || ""}
                  readOnly={isActivity()}
                  onClick={() => {
                    if (isActivity()) {
                      dispatch(
                        ActivityAction.showActivityDialog(
                          rowObject.get("itemId"),
                          rowObject.get("id")
                        )
                      );
                    } else {
                      navigateToItem({ item: rowObject, navigate });
                    }
                  }}
                  onEdit={(newValue) => {
                    if (currentVal != newValue) {
                      handleUpdateItem(fieldId, newValue);
                    }
                  }}
                />
              </div>
            )}
          </ActionableField>
        );
        break;
      case "summary":
        const isHoverable =
          rowObject.get("type") === "COMPANY" || rowObject.get("type") === "CONTACT";
        const isDeal = rowType === "SALE" || rowObject.get("type") === "SALE";
        const showAvatar = !isDeal;

        component = (
          <ActionableField
            showAddFilter={
              showAddRowFilter &&
              rowObject.get("type") !== "COMPANY" &&
              rowObject.get("type") !== "CONTACT"
            }
            onClickAddFilter={() => {
              if (currentVal) {
                handleAddFilter({
                  [fieldId]: {
                    operator: "in",
                    values: [currentVal],
                  },
                });
              }
            }}
          >
            {isHoverable ? (
              <HoverManager
                itemId={rowObject.get("id")}
                itemType={rowObject.get("type")}
                label={currentVal}
              >
                <div className={"flex items-center truncate w-full relative"}>
                  {showRowSelection && <SelectionField itemId={rowObject.get("id")} />}
                  {showAvatar && (
                    <>
                      {rowObject.get("iconUrl") ? (
                        <img
                          src={rowObject.get("iconUrl")}
                          alt="Account Logo"
                          className="w-5 h-5 rounded-lg object-cover ml-1.5"
                        />
                      ) : (
                        <DefaultAvatar
                          name={rowObject.get("name")}
                          type={rowObject.get("type")}
                          size="sm"
                          className="ml-1.5"
                        />
                      )}
                    </>
                  )}
                  <InputInline
                    readOnly={isActivity()}
                    onFinish={(newValue) => {
                      if (currentVal != newValue) {
                        handleUpdateItem(fieldId, newValue);
                      }
                    }}
                    value={currentVal}
                  />
                </div>
              </HoverManager>
            ) : (
              <div className={"flex items-center truncate w-full relative"}>
                {showRowSelection && <SelectionField itemId={rowObject.get("id")} />}
                {showAvatar && (
                  <>
                    {rowObject.get("iconUrl") ? (
                      <img
                        src={rowObject.get("iconUrl")}
                        alt="Account Logo"
                        className="w-5 h-5 rounded-lg object-cover ml-1.5"
                      />
                    ) : (
                      <DefaultAvatar
                        name={rowObject.get("name")}
                        type={rowObject.get("type")}
                        size="sm"
                        className="ml-1.5"
                      />
                    )}
                  </>
                )}
                <InputInline
                  readOnly={isActivity()}
                  onFinish={(newValue) => {
                    if (currentVal != newValue) {
                      handleUpdateItem(fieldId, newValue);
                    }
                  }}
                  value={currentVal}
                  rightAction={
                    <InformationCircleIcon
                      className={
                        "w-4 hover:text-blue-600 icon-action cursor-pointer absolute right-2 top-2"
                      }
                      onClick={() => {
                        if (isActivity()) {
                          dispatch(
                            ActivityAction.showActivityDialog(
                              rowObject.get("itemId"),
                              rowObject.get("id")
                            )
                          );
                        } else {
                          navigateToItem({ item: rowObject, navigate });
                        }
                      }}
                    />
                  }
                />
              </div>
            )}
          </ActionableField>
        );
        break;
      case "description":
      case "privateDescription":
        return (
          <div style={{ maxWidth: column.get("width") }} className={"w-full"}>
            <div
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                padding: "8px 12px",
              }}
              className="!max-h-6 !overflow-hidden !whitespace-nowrap"
            >
              <div style={{ overflow: "hidden" }}>
                <Editor
                  viewMode={true}
                  editorContent={{
                    htmlContent: currentVal || "",
                    contents: {},
                  }}
                />
              </div>
            </div>
          </div>
        );
      case "done":
        return (
          <div className={"w-full flex justify-center"}>
            <ActivityStatus
              activity={rowObject}
              itemId={rowObject.get("itemId")}
              onClickMarkAsDone={() => {
                if (onReload) onReload();
              }}
            />
          </div>
        );
      case "domains":
        const domains = toIdArray(currentVal);
        component = (
          <DomainsSelect
            type="GRID"
            value={domains || []}
            onChange={(domains: string[]) => handleUpdateItem(fieldId, domains)}
          />
        );
        break;
      case "assignees":
        const assigneeIds = toIdArray(currentVal);

        component = (
          <AssigneeSelectV2
            value={assigneeIds}
            showTriggerHeight={true}
            onChange={(ids) => {
              // Convert array to CSV string for changeUser
              const idsArray = Array.isArray(ids) ? ids : ids ? [ids] : [];
              changeUser(idsArray.map((accountId) => ({ accountId })));
            }}
            multiple={true}
            placeholder="Assign to..."
            className="w-full"
            noBorder={true}
            hideIndicatorUntilHover={true}
          />
        );
        break;
      case "finish":
      case "birthday":
      case "start":
      case "markedAsDoneTime":
      case "updatedTime":
      case "nextActivityDatetime":
        const date = new Date(currentVal);
        if (isValid(date)) {
          const dateFormat = column.get("format") || "dd-MM-yyyy";
          if (dateFormat == "formatDistanceToNow") {
            component = (
              <div className="text-sm font-medium text-gray-700 leading-5 tracking-tight">
                {formatDistanceToNow(date)}
              </div>
            );
          } else {
            component = (
              <div className="text-sm font-medium text-gray-700 leading-5 tracking-tight">
                {format(date, dateFormat)}
              </div>
            );
          }
        }
        break;
      case "estimatedProbability":
        component = <ProgressField value={currentVal} />;
        break;
      case "phone":
        component = (
          <div className="text-sm font-medium text-gray-700 leading-5 tracking-tight">
            <InputInline
              onFinish={(newValue) => {
                if (currentVal != newValue) {
                  handleUpdateItem(column.get("selector"), newValue);
                }
              }}
              value={currentVal}
              placeholder="Phone..."
            />
          </div>
        );
        break;
      case "email":
        component = (
          <EmailInputField
            onChange={(value, hasError) => {
              setEmailError(hasError);
            }}
            onFinish={(newValue, hasError) => {
              setEmailError(hasError);
              if (!hasError && currentVal != newValue) {
                handleUpdateItem(column.get("selector"), newValue);
              }
            }}
            error={emailError}
            value={currentVal}
            isGrid={true}
          />
        );
        break;
      case "website":
        component = (
          <WebsiteInputField
            onChange={(value, hasError) => {
              setWebsiteError(hasError);
            }}
            onFinish={(newValue, hasError) => {
              setWebsiteError(hasError);
              if (!hasError && currentVal != newValue) {
                handleUpdateItem(column.get("selector"), newValue);
              }
            }}
            error={websiteError}
            value={currentVal}
            isGrid={true}
          />
        );
        break;
      case "jobTitle":
      case "linkedIn":
      case "angleList":
      case "instagram":
      case "facebook":
      case "twitter":
        component = (
          <ActionableField
            showAddFilter={
              showAddRowFilter &&
              rowObject.get("type") !== "COMPANY" &&
              rowObject.get("type") !== "CONTACT"
            }
            onClickAddFilter={() => {
              if (currentVal) {
                handleAddFilter({
                  [fieldId]: {
                    operator: "in",
                    values: [currentVal],
                  },
                });
              }
            }}
          >
            <div
              className={
                "w-full hover:text-blue-600 text-sm font-medium text-gray-700 leading-5 tracking-tight"
              }
            >
              <InputInline
                onFinish={(newValue) => {
                  if (currentVal != newValue) {
                    handleUpdateItem(column.get("selector"), newValue);
                  }
                }}
                value={currentVal}
              />
            </div>
          </ActionableField>
        );
        break;
      case "estimatedRevenue":
      case "price":
      case "unitPrice":
        return (
          <div className="w-full h-full text-center text-sm font-medium text-gray-700 leading-5 tracking-tight truncate">
            <AmountField amount={currentVal} />
          </div>
        );
      case "stage":
      case "status":
        const options = getRuleFieldValues(fieldId);
        const selectedOption = options.find((item: any) => item.id == currentVal) || {};
        const currentColorStatus = colorCodes.find((cc) => cc.color === selectedOption.color);
        const colorCodeStatus = currentColorStatus?.code;
        component = (
          <ActionableField
            showAddFilter={
              showAddRowFilter &&
              rowObject.get("type") !== "COMPANY" &&
              rowObject.get("type") !== "CONTACT"
            }
            onClickAddFilter={() => {
              if (selectedOption.id) {
                handleAddFilter({
                  [fieldId]: {
                    operator: "in",
                    values: [currentVal],
                  },
                });
              }
            }}
          >
            <div className="flex items-center gap-1">
              <span
                className="block w-3.5 h-3.5 rounded-full shrink-0"
                style={{ backgroundColor: colorCodeStatus || "#d1d5db" }}
              />
              <DropdownField
                showColor={true}
                value={currentVal}
                options={options}
                hideIndicatorUntilHover={true}
                onChange={async (newValue: string) => {
                  if (newValue !== currentVal) {
                    handleUpdateItem(fieldId, newValue);
                  }
                }}
              />
            </div>
          </ActionableField>
        );
        break;
      case "productCode":
      case "unit":
      case "street":
      case "postcode":
      case "billingStreet":
      case "billingPostcode":
        component = (
          <div className="text-sm font-medium text-gray-700 leading-5 tracking-tight">
            <InputInline
              onFinish={(newValue) => {
                if (currentVal != newValue) {
                  handleUpdateItem(column.get("selector"), newValue);
                }
              }}
              value={currentVal}
            />
          </div>
        );
        break;
      case "outlineNumber":
        component = <div>{outlineNum}</div>;
        break;
      case "salutation":
        component = (
          <DropdownField
            leftIcon={<UserIcon width={20} />}
            defaultValue={{ name: "" }}
            value={currentVal}
            options={getRuleFieldValues("salutation")}
            hideIndicatorUntilHover={true}
            onChange={async (id: string) => {
              if (id !== currentVal) {
                handleUpdateItem("salutation", id);
              }
            }}
          />
        );
        break;
      case "bucketId":
        const selectedBucket = buckets.find((item) => item.get("id") == currentVal) || fromJS({});
        const currentColorBucketId = colorCodes.find(
          (cc) => cc.color === selectedBucket.get("color")
        );
        const colorCodeBucketId = currentColorBucketId?.code;
        component = (
          <ActionableField
            showAddFilter={
              showAddRowFilter &&
              rowObject.get("type") !== "COMPANY" &&
              rowObject.get("type") !== "CONTACT"
            }
            onClickAddFilter={() => {
              if (selectedBucket.get("id")) {
                handleAddFilter({
                  bucketId: {
                    operator: "in",
                    values: [currentVal],
                  },
                });
              }
            }}
          >
            <>
              <div className="flex items-center gap-1 bucket-dropdown-wrapper">
                <span
                  className="block w-3.5 h-3.5 rounded-full shrink-0"
                  style={{ backgroundColor: colorCodeBucketId || "#d1d5db" }}
                />
                <DropdownField
                  showColor={true}
                  value={currentVal}
                  options={buckets && buckets.toJS()}
                  hideIndicatorUntilHover={true}
                  onChange={(id: string) => {
                    if (id !== currentVal) {
                      handleUpdateItem("bucketId", id);
                    }
                  }}
                />
              </div>
              <style>{`
                .bucket-dropdown-wrapper [role="menu"] {
                  min-width: 280px;
                }
                .bucket-dropdown-wrapper [role="listbox"] {
                  min-width: 280px !important;
                  width: auto !important;
                }
                .bucket-dropdown-wrapper .css-qr46ko {
                  min-width: 280px !important;
                  width: auto !important;
                }
                .bucket-dropdown-wrapper [id*="react-select"] {
                  min-width: 280px !important;
                  width: auto !important;
                }
                .bucket-dropdown-wrapper [class*="menu"] {
                  min-width: 280px !important;
                }
              `}</style>
            </>
          </ActionableField>
        );
        break;
      case "account":
        const accountValue = currentVal?.get ? currentVal.get("value") : currentVal?.value;
        const accountLabel = currentVal?.get ? currentVal.get("label") : currentVal?.label;
        const accountWidth = "233px";

        // Render account label with avatar
        const renderAccountLabel = () => {
          if (!accountValue || !accountLabel) {
            return <span className="text-gray-400 text-sm ml-1">Select account</span>;
          }

          const accountItem = currentVal?.toJS ? currentVal.toJS() : currentVal;
          const icon = renderItemIcon(accountItem, "COMPANY", "w-[20px] h-[20px]");

          return (
            <div className="flex items-center gap-2 flex-1 min-w-0 min-h-[32px] ml-1">
              {icon && <div className="shrink-0">{icon}</div>}
              <span className="text-[12px] font-medium leading-[16px] truncate flex-1 min-w-0 text-left text-[#1f2937]">
                {accountLabel}
              </span>
            </div>
          );
        };

        component = (
          <ActionableField
            showAddFilter={
              showAddRowFilter &&
              rowObject.get("type") !== "COMPANY" &&
              rowObject.get("type") !== "CONTACT"
            }
            onClickAddFilter={() => {
              if (currentVal) {
                const value = currentVal.get("value");
                handleAddFilter({
                  [fieldId]: {
                    operator: "in",
                    values: [value],
                  },
                });
              }
            }}
          >
            <div className="link-item-dropdown-wrapper">
              {accountValue ? (
                <HoverManager itemId={accountValue} itemType="COMPANY" label={accountLabel}>
                  <LinkItemPickerV2
                    showSelectIcon={true}
                    hideSelectIconUntilHover={true}
                    showTriggerHeight={true}
                    customTrigger={renderAccountLabel()}
                    onChange={(item) => {
                      handleUpdateItem(fieldId, item);
                    }}
                    maxWidth={accountWidth}
                    type="COMPANY"
                    projectId={rowObject.get("projectId")}
                    items={currentVal}
                    showInfoIcon={false}
                    noBorder={true}
                  />
                </HoverManager>
              ) : (
                <LinkItemPickerV2
                  showSelectIcon={true}
                  hideSelectIconUntilHover={true}
                  showTriggerHeight={true}
                  customTrigger={renderAccountLabel()}
                  onChange={(item) => {
                    handleUpdateItem(fieldId, item);
                  }}
                  maxWidth={accountWidth}
                  type="COMPANY"
                  projectId={rowObject.get("projectId")}
                  items={currentVal}
                  showInfoIcon={false}
                  noBorder={true}
                />
              )}
            </div>
            <style>{`
              .link-item-dropdown-wrapper [role="listbox"] {
                min-width: ${accountWidth} !important;
                width: ${accountWidth} !important;
                max-width: ${accountWidth} !important;
              }
              .link-item-dropdown-wrapper .css-qr46ko {
                min-width: ${accountWidth} !important;
                width: ${accountWidth} !important;
                max-width: ${accountWidth} !important;
              }
              .link-item-dropdown-wrapper [id*="react-select"] {
                min-width: ${accountWidth} !important;
                width: ${accountWidth} !important;
                max-width: ${accountWidth} !important;
              }
              .link-item-dropdown-wrapper [class*="menu"] {
                min-width: ${accountWidth} !important;
                width: ${accountWidth} !important;
                max-width: ${accountWidth} !important;
              }
            `}</style>
          </ActionableField>
        );
        break;
      case "contact":
        const contactValue = currentVal?.get ? currentVal.get("value") : currentVal?.value;
        const contactLabel = currentVal?.get ? currentVal.get("label") : currentVal?.label;
        const contactWidth = "233px";

        // Render contact label with avatar
        const renderContactLabel = () => {
          if (!contactValue || !contactLabel) {
            return <span className="text-gray-400 text-sm ml-1.5">Select contact</span>;
          }

          const contactItem = currentVal?.toJS ? currentVal.toJS() : currentVal;
          const icon = renderItemIcon(contactItem, "CONTACT", "w-[20px] h-[20px]");

          return (
            <div className="flex items-center gap-2 flex-1 min-w-0 min-h-[32px] ml-1.5">
              {icon && <div className="shrink-0">{icon}</div>}
              <span className="text-[12px] font-medium leading-[16px] truncate flex-1 min-w-0 text-left text-[#1f2937]">
                {contactLabel}
              </span>
            </div>
          );
        };

        component = (
          <ActionableField
            showAddFilter={
              showAddRowFilter &&
              rowObject.get("type") !== "COMPANY" &&
              rowObject.get("type") !== "CONTACT"
            }
            onClickAddFilter={() => {
              if (currentVal) {
                const value = currentVal.get("value");
                handleAddFilter({
                  [fieldId]: {
                    operator: "in",
                    values: [value],
                  },
                });
              }
            }}
          >
            <div className="contact-dropdown-wrapper">
              {contactValue ? (
                <HoverManager itemId={contactValue} itemType="CONTACT" label={contactLabel}>
                  <LinkItemPickerV2
                    showTriggerHeight={true}
                    customTrigger={renderContactLabel()}
                    onChange={(item) => {
                      handleUpdateItem(fieldId, item);
                    }}
                    maxWidth={contactWidth}
                    type="CONTACT"
                    projectId={rowObject.get("projectId")}
                    items={currentVal}
                    showInfoIcon={false}
                    showSelectIcon={true}
                    hideSelectIconUntilHover={true}
                    noBorder={true}
                  />
                </HoverManager>
              ) : (
                <LinkItemPickerV2
                  showTriggerHeight={true}
                  customTrigger={renderContactLabel()}
                  onChange={(item) => {
                    handleUpdateItem(fieldId, item);
                  }}
                  maxWidth={contactWidth}
                  type="CONTACT"
                  projectId={rowObject.get("projectId")}
                  items={currentVal}
                  showInfoIcon={false}
                  showSelectIcon={true}
                  hideSelectIconUntilHover={true}
                  noBorder={true}
                />
              )}
            </div>
            <style>{`
              .contact-dropdown-wrapper [role="listbox"] {
                min-width: ${contactWidth} !important;
                width: ${contactWidth} !important;
                max-width: ${contactWidth} !important;
              }
              .contact-dropdown-wrapper .css-qr46ko {
                min-width: ${contactWidth} !important;
                width: ${contactWidth} !important;
                max-width: ${contactWidth} !important;
              }
              .contact-dropdown-wrapper [id*="react-select"] {
                min-width: ${contactWidth} !important;
                width: ${contactWidth} !important;
                max-width: ${contactWidth} !important;
              }
              .contact-dropdown-wrapper [class*="menu"] {
                min-width: ${contactWidth} !important;
                width: ${contactWidth} !important;
                max-width: ${contactWidth} !important;
              }
            `}</style>
          </ActionableField>
        );
        break;
      case "sale":
      case "issue":
        let dataType = "TASK";
        if (fieldId == "sale") {
          dataType = "SALE";
        }
        const linkItemWidth = field.get("width") || "233px";
        component = (
          <ActionableField
            showAddFilter={
              showAddRowFilter &&
              rowObject.get("type") !== "COMPANY" &&
              rowObject.get("type") !== "CONTACT"
            }
            onClickAddFilter={() => {
              if (currentVal) {
                const value = currentVal.get("value");
                handleAddFilter({
                  [fieldId]: {
                    operator: "in",
                    values: [value],
                  },
                });
              }
            }}
          >
            <div className="link-item-dropdown-wrapper">
              <LinkItemPickerV2
                showSelectIcon={true}
                hideSelectIconUntilHover={true}
                showTriggerHeight={true}
                onChange={(item) => {
                  handleUpdateItem(fieldId, item);
                }}
                maxWidth={linkItemWidth}
                type={dataType}
                projectId={rowObject.get("projectId")}
                items={currentVal}
                noBorder={true}
              />
            </div>
            <style>{`
              .link-item-dropdown-wrapper [role="listbox"] {
                min-width: ${linkItemWidth} !important;
                width: ${linkItemWidth} !important;
                max-width: ${linkItemWidth} !important;
              }
              .link-item-dropdown-wrapper .css-qr46ko {
                min-width: ${linkItemWidth} !important;
                width: ${linkItemWidth} !important;
                max-width: ${linkItemWidth} !important;
              }
              .link-item-dropdown-wrapper [id*="react-select"] {
                min-width: ${linkItemWidth} !important;
                width: ${linkItemWidth} !important;
                max-width: ${linkItemWidth} !important;
              }
              .link-item-dropdown-wrapper [class*="menu"] {
                min-width: ${linkItemWidth} !important;
                width: ${linkItemWidth} !important;
                max-width: ${linkItemWidth} !important;
              }
            `}</style>
          </ActionableField>
        );
        break;
      case "item":
        component = (
          <div
            className="w-full h-full flex gap-2 items-center text-sm font-medium text-gray-700 leading-5 tracking-tight"
            data-tooltip-id={"information-tooltip"}
            onMouseEnter={() => {
              if (rowType !== "COMPANY" && rowType !== "CONTACT") {
                dispatch(
                  LayoutAction.hoverDataItem({
                    value: rowObject.getIn(["event", "data", column.get("selector"), "value"]),
                    label: currentVal,
                    type: rowType,
                    isOpen: true,
                  })
                );
              }
            }}
          >
            {renderItemIcon({}, rowObject.getIn(["event", "data", column.get("selector"), "type"]))}
            {currentVal}
          </div>
        );
        break;
      // Interaction fields
      case "firstComment":
      case "lastComment":
      case "firstEmail":
      case "lastEmail":
      case "lastActivity":
      case "firstActivity":
      case "firstMeeting":
      case "lastMeeting":
      case "firstCall":
      case "lastCall":
      case "lastMeetingInPerson":
      case "firstMeetingInPerson":
      case "firstTask":
      case "lastTask":
      case "firstDeadline":
      case "lastDeadline":
        const startDate =
          rowObject.getIn(["data", column.get("selector"), "activity", "event", "data", "start"]) ||
          "";
        const startTime =
          rowObject.getIn([
            "data",
            column.get("selector"),
            "activity",
            "event",
            "data",
            "startTime",
          ]) || "";
        const firstMeetingDate = new Date(`${startDate} ${startTime}`);
        if (isValid(firstMeetingDate)) {
          component = (
            <div className="text-sm font-medium text-gray-700 leading-5 tracking-tight">
              {format(firstMeetingDate, "dd-MM-yyyy")}
            </div>
          );
        } else {
          component = (
            <div className="text-sm font-medium text-gray-500 leading-5 tracking-tight h-full w-full flex items-center">
              No communication
            </div>
          );
        }
        break;
      case "connectionStrength":
        const strengthValue = rowObject.getIn(["data", column.get("selector"), "value"]) || 0;
        component = (
          <div className={"flex truncate justify-center items-center"}>
            {renderConnectionStrength(strengthValue)}
          </div>
        );
        break;

      // Company fields
      case "country":
      case "city":
      case "state":
        component = (
          <div className="flex truncate justify-center items-center text-sm font-medium text-gray-700 leading-5 tracking-tight">
            {rowObject.getIn(["data", column.get("selector"), "value", "label"])}
          </div>
        );
        break;
      case "products": {
        const productsRaw = currentVal;
        const productsList = productsRaw && (productsRaw.toJS ? productsRaw.toJS() : productsRaw);
        const productsArray = Array.isArray(productsList) ? productsList : [];
        const productLabels =
          productsArray.length === 0
            ? null
            : productsArray
                .map((p: { name?: string; productCode?: string; quantity?: number }) => {
                  const name = p?.name ?? p?.productCode ?? "—";
                  const qty = p?.quantity ?? 1;
                  return qty > 1 ? `${name} × ${qty}` : name;
                })
                .join(", ");
        component = (
          <div className="flex truncate items-center text-sm font-medium text-gray-700 leading-5 tracking-tight ml-1.5">
            {productLabels ?? ""}
          </div>
        );
        break;
      }
      case "labels":
        component = (
          <LabelsSelect
            projectId={rowObject.get("projectId")}
            contentType={rowType}
            type="GRID"
            value={currentVal ? currentVal.toJS() : []}
            onChange={(labelIds: string[]) => handleUpdateItem("labels", labelIds)}
            placeholder="Select labels"
          />
        );
        break;
      default:
        component = (
          <div className="flex justify-center items-center text-center w-full h-full text-sm font-medium text-gray-700 leading-5 tracking-tight">
            {currentVal || <span className="text-gray-300">—</span>}
          </div>
        );
        break;
    }
    return (
      <div
        className={
          "w-full h-full flex items-center text-sm font-medium text-gray-700 leading-5 tracking-tight max-w-[412px]"
        }
      >
        {component}
      </div>
    );
  };

  return renderView();
};

export default CellRenderer;
